import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { DatePicker, Image, Upload as AntUpload } from "antd";
import Checkbox from "antd/es/checkbox/Checkbox";
import { Eye, FileText, GraduationCap, Plus, Save, Trash, UploadCloudIcon } from "lucide-react";
import dayjs from "dayjs";

import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";
import { ConfirmPopupModal } from "../../../components/ui/dialogModal";
import { canAccessEmployeeProfile } from "../../../utils/employeeProfileAccess";
import { EmployeeProfileViewOnlyBanner } from "../../../components/employee/EmployeeProfileAccessNotice";
import ToastMsg from "../../../components/common/ToastMsg";
import useApiMethods from "../../../lib/useApiMethods";
import { EMPLOYEE_CONFIRM_PERMANENT, EMPLOYEE_QUALIFICATION_DOC } from "../../../constants/api";
import { cn } from "../../../utils/tailwindUtils";

const createEmptyDocument = () => ({
    key: `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    id: null,
    document_name: "",
    document_description: "",
    document_date: null,
    is_verified: false,
    hard_copy_collected: false,
    document_file: [],
    dirty: true,
});

const mapApiDocument = (item) => ({
    key: `saved-${item.id}`,
    id: item.id,
    document_name: item.document_name || "",
    document_description: item.document_description || "",
    document_date: item.document_date ? dayjs(item.document_date) : null,
    is_verified: Boolean(item.is_verified),
    hard_copy_collected: Boolean(item.hard_copy_collected),
    document_file: item.document_url ? [item.document_url] : [],
    remove_file: false,
    dirty: false,
});

const QualificationDocumentContent = forwardRef(({ employeeID }, ref) => {
    const { fetch, create, update, remove } = useApiMethods();
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [confirmation, setConfirmation] = useState({
        is_permanent: false,
        actual_date_of_joining: null,
        employee_type_name: null,
    });
    const [confirmPermanentOpen, setConfirmPermanentOpen] = useState(false);
    const [confirmingPermanent, setConfirmingPermanent] = useState(false);
    const deletedIdsRef = useRef(new Set());
    const canEditDocuments = canAccessEmployeeProfile("documents", "edit");

    const loadDocuments = async () => {
        if (!employeeID) return;
        setLoading(true);
        try {
            const apiData = await fetch(EMPLOYEE_QUALIFICATION_DOC, { employee_id: employeeID });
            if (apiData?.status === "success") {
                setDocuments((apiData.data || []).map(mapApiDocument));
                if (apiData.confirmation) {
                    setConfirmation({
                        is_permanent: Boolean(apiData.confirmation.is_permanent),
                        actual_date_of_joining: apiData.confirmation.actual_date_of_joining || null,
                        employee_type_name: apiData.confirmation.employee_type_name || null,
                    });
                }
            } else {
                ToastMsg("error", apiData?.message || "Failed to load qualification documents");
            }
        } catch (error) {
            ToastMsg("error", error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (employeeID) {
            loadDocuments();
        }
        // fetch from useApiMethods is not referentially stable
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [employeeID]);

    const handleFieldChange = (key, field, value) => {
        setDocuments((prev) => prev.map((doc) => (
            doc.key === key ? { ...doc, [field]: value, dirty: true } : doc
        )));
    };

    const handleNewDocument = () => {
        setDocuments((prev) => [...prev, createEmptyDocument()]);
    };

    const handleRemoveUnsaved = (key) => {
        setDocuments((prev) => prev.filter((doc) => doc.key !== key));
    };

    const validateDocument = (doc, index) => {
        if (!doc.document_name?.trim()) {
            return `Document name is required for document ${index + 1}`;
        }
        if (!doc.document_date) {
            return `Date is required for document ${index + 1}`;
        }
        const hasFile = Array.isArray(doc.document_file) && doc.document_file.length > 0;
        if (!doc.id && !hasFile) {
            return `Please upload a file for document ${index + 1}`;
        }
        return null;
    };

    const persistDocument = async (doc) => {
        const payload = new FormData();
        payload.append("employee_id", employeeID);
        payload.append("document_name", doc.document_name.trim());
        payload.append("document_description", (doc.document_description || "").trim());
        payload.append("document_date", doc.document_date.format("YYYY-MM-DD"));
        payload.append("is_verified", doc.is_verified ? "true" : "false");
        payload.append("hard_copy_collected", doc.hard_copy_collected ? "true" : "false");

        const file = doc.document_file?.[0];
        if (file instanceof File) {
            payload.append("document", file);
        } else if (doc.remove_file) {
            payload.append("remove_document", "true");
        }

        if (doc.id) {
            payload.append("id", doc.id);
            return update(`/${EMPLOYEE_QUALIFICATION_DOC}`, payload, true);
        }
        return create(`/${EMPLOYEE_QUALIFICATION_DOC}`, payload, true);
    };

    const handleSave = async () => {
        if (!canEditDocuments) return true;
        const dirtyDocs = documents.filter((doc) => doc.dirty && !deletedIdsRef.current.has(doc.id));
        if (dirtyDocs.length === 0) {
            return true;
        }

        for (let index = 0; index < documents.length; index += 1) {
            const doc = documents[index];
            if (!doc.dirty || deletedIdsRef.current.has(doc.id)) continue;
            const error = validateDocument(doc, index);
            if (error) {
                ToastMsg("error", error);
                return false;
            }
        }

        setSaving(true);
        try {
            for (const doc of dirtyDocs) {
                const response = await persistDocument(doc);
                if (response?.status === "fail") {
                    ToastMsg("error", response.message || "Failed to save qualification document");
                    return false;
                }
            }
            ToastMsg("success", "Qualification documents saved successfully");
            await loadDocuments();
            return true;
        } catch (error) {
            ToastMsg("error", error.message || "An error occurred while saving");
            return false;
        } finally {
            setSaving(false);
        }
    };

    useImperativeHandle(ref, () => ({
        handleSave,
    }));

    const handleRevertPermanent = async () => {
        setConfirmingPermanent(true);
        try {
            const response = await create(`/${EMPLOYEE_CONFIRM_PERMANENT}`, {
                employee_id: employeeID,
                enabled: false,
            });
            if (response?.status === "success") {
                setConfirmation(response.confirmation || {
                    is_permanent: false,
                    actual_date_of_joining: null,
                });
                ToastMsg("success", response.message || "Permanent confirmation turned off");
            } else {
                ToastMsg("error", response?.message || "Failed to turn off permanent confirmation");
            }
        } catch (error) {
            ToastMsg("error", error.message || "Failed to turn off permanent confirmation");
        } finally {
            setConfirmingPermanent(false);
        }
    };

    const handlePermanentToggle = (checked) => {
        if (!checked) {
            handleRevertPermanent();
            return;
        }
        if (confirmation.is_permanent) {
            return;
        }
        setConfirmPermanentOpen(true);
    };

    const handleConfirmPermanent = async () => {
        setConfirmingPermanent(true);
        try {
            const response = await create(`/${EMPLOYEE_CONFIRM_PERMANENT}`, {
                employee_id: employeeID,
            });
            if (response?.status === "success") {
                if (response.confirmation) {
                    setConfirmation(response.confirmation);
                } else {
                    setConfirmation((prev) => ({
                        ...prev,
                        is_permanent: true,
                        actual_date_of_joining: dayjs().format("YYYY-MM-DD"),
                    }));
                }
                setConfirmPermanentOpen(false);
                ToastMsg("success", response.message || "Employee marked as permanent");
            } else {
                ToastMsg("error", response?.message || "Failed to mark employee as permanent");
            }
        } catch (error) {
            ToastMsg("error", error.message || "Failed to mark employee as permanent");
        } finally {
            setConfirmingPermanent(false);
        }
    };

    const handleRemoveFile = (key) => {
        setDocuments((prev) => prev.map((doc) => (
            doc.key === key
                ? { ...doc, document_file: [], remove_file: true, dirty: true }
                : doc
        )));
    };

    const handleConfirmDelete = async () => {
        if (!deleteTarget?.id) return;
        const targetId = deleteTarget.id;
        deletedIdsRef.current.add(targetId);
        setDocuments((prev) => prev.filter((doc) => doc.id !== targetId));
        setDeleteTarget(null);
        setDeleting(true);
        try {
            const response = await remove(`/${EMPLOYEE_QUALIFICATION_DOC}?id=${targetId}`);
            if (response?.status === "success") {
                ToastMsg("success", response.message || "Document deleted successfully");
                await loadDocuments();
            } else {
                deletedIdsRef.current.delete(targetId);
                ToastMsg("error", response?.message || "Failed to delete document");
                await loadDocuments();
            }
        } catch (error) {
            deletedIdsRef.current.delete(targetId);
            ToastMsg("error", error.message || "An error occurred while deleting");
            await loadDocuments();
        } finally {
            setDeleting(false);
        }
    };

    if (loading && documents.length === 0) {
        return (
            <div className="h-[calc(100vh-260px)] flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-primary" />
            </div>
        );
    }

    const hasDirty = documents.some((doc) => doc.dirty);

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-start gap-4">
                <div>
                    <h2 className="text-md font-bold text-gray-900 dark:text-white">
                        Qualification Documents
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Upload degree, licence, and other qualification documents. Add as many as needed.
                    </p>
                </div>
                {canEditDocuments && (
                    <Button
                        type="button"
                        variant="outline"
                        onClick={handleNewDocument}
                        className="flex items-center gap-2 shrink-0"
                    >
                        <Plus className="w-4 h-4" />
                        New Document
                    </Button>
                )}
            </div>

            <div className="flex items-center justify-between gap-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3">
                <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                        Permanent employee
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {confirmation.is_permanent
                            ? `Actual DOJ ${confirmation.actual_date_of_joining ? dayjs(confirmation.actual_date_of_joining).format("DD-MM-YYYY") : "is set"}. Turn off to restore the previous joining date.`
                            : "Turn on to mark this employee permanent and set Actual DOJ to today."}
                    </p>
                </div>
                <VerifiedToggle
                    checked={Boolean(confirmation.is_permanent)}
                    onChange={canEditDocuments ? handlePermanentToggle : undefined}
                    disabled={!canEditDocuments || confirmingPermanent}
                />
            </div>

            {!canEditDocuments && <EmployeeProfileViewOnlyBanner sectionLabel="qualification documents" />}

            {documents.length === 0 ? (
                <div className="text-center py-16 text-gray-500 dark:text-gray-400 border border-dashed border-gray-200 dark:border-gray-700 rounded-xl">
                    <GraduationCap className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No qualification documents added yet</p>
                    {canEditDocuments ? (
                    <>
                    <p className="text-sm mb-4">Use New Document to add a certificate or registration</p>
                    <Button type="button" variant="outline" onClick={handleNewDocument} className="inline-flex items-center gap-2">
                        <Plus className="w-4 h-4" />
                        New Document
                    </Button>
                    </>
                    ) : (
                    <p className="text-sm mt-1">No documents to display</p>
                    )}
                </div>
            ) : (
                <div className="space-y-4">
                    {documents.map((doc, index) => (
                        <div
                            key={doc.key}
                            className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden"
                        >
                            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-100 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-800/80">
                                <div className="flex items-center gap-2 min-w-0">
                                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-sidebar/10 text-sidebar-primary text-xs font-semibold">
                                        {index + 1}
                                    </span>
                                    <span className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                                        {doc.document_name?.trim() || `Document ${index + 1}`}
                                    </span>
                                    {doc.id && !doc.dirty && (
                                        <span className={cn(
                                            "inline-flex shrink-0 items-center px-2 py-0.5 rounded-full text-xs font-medium",
                                            doc.is_verified
                                                ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                                                : "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
                                        )}>
                                            {doc.is_verified ? "Verified" : "Not Verified"}
                                        </span>
                                    )}
                                    {doc.dirty && (
                                        <span className="inline-flex shrink-0 items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                                            Unsaved
                                        </span>
                                    )}
                                </div>
                                {canEditDocuments && (
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                        if (doc.id) {
                                            setDeleteTarget(doc);
                                        } else {
                                            handleRemoveUnsaved(doc.key);
                                        }
                                    }}
                                    className="p-2 shrink-0 text-red-600 border-red-200 hover:bg-red-50 dark:border-red-800 dark:hover:bg-red-900/20"
                                >
                                    <Trash className="w-4 h-4" />
                                </Button>
                                )}
                            </div>

                            <div className="p-4 space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <FormField label="Document Name" required className="md:col-span-2">
                                        <Input
                                            value={doc.document_name}
                                            onChange={(e) => handleFieldChange(doc.key, "document_name", e.target.value)}
                                            placeholder="e.g. Degree Certificate"
                                            disabled={!canEditDocuments}
                                            readOnly={!canEditDocuments}
                                            className="h-9 bg-background"
                                        />
                                    </FormField>

                                    <FormField label="Date" required>
                                        <DatePicker
                                            allowClear={canEditDocuments}
                                            format="DD-MM-YYYY"
                                            value={doc.document_date}
                                            onChange={(date) => handleFieldChange(doc.key, "document_date", date)}
                                            className="w-full"
                                            disabled={!canEditDocuments}
                                            placeholder="Select date"
                                        />
                                    </FormField>
                                </div>

                                <FormField label="Document Description">
                                    <Input
                                        value={doc.document_description}
                                        onChange={(e) => handleFieldChange(doc.key, "document_description", e.target.value)}
                                        placeholder="Enter description"
                                        disabled={!canEditDocuments}
                                        readOnly={!canEditDocuments}
                                        className="h-9 bg-background"
                                    />
                                </FormField>

                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                    <FormField label="Upload Document" required={!doc.id} className="min-w-0">
                                        <DocFileUpload
                                            readOnly={!canEditDocuments}
                                            label={doc.document_name}
                                            attachment={doc.document_file}
                                            onFileChange={(file) => handleFieldChange(doc.key, "document_file", file ? [file] : [])}
                                            onRemoveFile={() => handleRemoveFile(doc.key)}
                                        />
                                    </FormField>

                                    <div className="min-w-0 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/70 dark:bg-gray-900/30 p-3 space-y-3 self-end">
                                        <div className="flex items-center justify-between gap-3">
                                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                Verification
                                            </span>
                                            <div className="flex items-center gap-2">
                                                <span className={cn(
                                                    "text-xs whitespace-nowrap",
                                                    !doc.is_verified
                                                        ? "font-medium text-gray-900 dark:text-white"
                                                        : "text-gray-500 dark:text-gray-400"
                                                )}>
                                                    Not Verified
                                                </span>
                                                <VerifiedToggle
                                                    checked={doc.is_verified}
                                                    disabled={!canEditDocuments}
                                                    onChange={(checked) => handleFieldChange(doc.key, "is_verified", checked)}
                                                />
                                                <span className={cn(
                                                    "text-xs whitespace-nowrap",
                                                    doc.is_verified
                                                        ? "font-medium text-gray-900 dark:text-white"
                                                        : "text-gray-500 dark:text-gray-400"
                                                )}>
                                                    Verified
                                                </span>
                                            </div>
                                        </div>
                                        <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
                                            <Checkbox
                                                checked={!!doc.hard_copy_collected}
                                                disabled={!canEditDocuments}
                                                onChange={(e) => handleFieldChange(doc.key, "hard_copy_collected", e.target.checked)}
                                            >
                                                <span className="text-sm text-gray-600 dark:text-gray-400">
                                                    Hard copy collected
                                                </span>
                                            </Checkbox>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}

                    {canEditDocuments && (
                    <div className="flex justify-end items-center gap-3 p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                        <Button
                            variant="brand"
                            disabled={saving || !hasDirty}
                            onClick={handleSave}
                            className="flex items-center gap-2 transition-colors duration-200"
                        >
                            {saving ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                            ) : (
                                <Save className="w-4 h-4" />
                            )}
                            {saving ? "Saving..." : "Save Documents"}
                        </Button>
                    </div>
                    )}
                </div>
            )}

            <ConfirmPopupModal
                open={confirmPermanentOpen}
                onClose={() => {
                    if (!confirmingPermanent) {
                        setConfirmPermanentOpen(false);
                    }
                }}
                onConfirm={handleConfirmPermanent}
                title="Confirm permanent employee"
                description={`Mark this employee as permanent? Actual date of joining will be set to today (${dayjs().format("DD-MM-YYYY")}).`}
                type="info"
                confirmLabel="OK"
                loading={confirmingPermanent}
            />

            <ConfirmPopupModal
                open={Boolean(deleteTarget)}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleConfirmDelete}
                title="Delete qualification document"
                description="Are you sure you want to delete this qualification document? This action cannot be undone."
                type="warning"
                confirmLabel="Delete"
                destructive={true}
                loading={deleting}
            />
        </div>
    );
});

const FormField = ({ label, required = false, children, className }) => (
    <div className={cn("space-y-2", className)}>
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1">
            {label}
            {required && <span className="text-red-500 text-xs">*</span>}
        </label>
        {children}
    </div>
);

const VerifiedToggle = ({ checked, onChange, disabled = false }) => (
    <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => {
            if (disabled || !onChange) return;
            onChange(!checked);
        }}
        className={cn(
            "relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-sidebar-primary focus:ring-offset-2 dark:focus:ring-offset-gray-800",
            checked ? "bg-sidebar" : "bg-gray-300 dark:bg-gray-600",
            disabled && "opacity-60 cursor-not-allowed"
        )}
    >
        <span
            className={cn(
                "inline-block h-5 w-5 transform rounded-full bg-white transition-transform",
                checked ? "translate-x-[22px]" : "translate-x-0.5"
            )}
        />
    </button>
);

const displayFileLabel = (file, fileUrl, isImageFile, documentLabel) => {
    if (file instanceof File && file.name) {
        return file.name;
    }
    const name = String(documentLabel || "").trim();
    if (name) return name;
    const path = String(fileUrl || "").split("?")[0].split("#")[0];
    const ext = (path.match(/\.([a-z0-9]{1,5})$/i) || [])[1]?.toUpperCase();
    if (isImageFile) return ext ? `${ext} image` : "Image";
    if (ext === "PDF") return "PDF document";
    if (ext === "DOC" || ext === "DOCX") return "Word document";
    return ext ? `${ext} document` : "Uploaded document";
};

const DocFileUpload = ({ attachment, onFileChange, onRemoveFile, compact = false, readOnly = false, label = "" }) => {
    const [previewOpen, setPreviewOpen] = useState(false);
    const isFileObject = attachment && Array.isArray(attachment) && attachment.length > 0 && attachment[0] instanceof File;
    const isStringUrl = attachment && Array.isArray(attachment) && attachment.length > 0 && typeof attachment[0] === "string";
    const hasAttachment = isFileObject || isStringUrl;

    if (!hasAttachment) {
        if (readOnly) {
            return <p className="text-xs text-gray-400 dark:text-gray-500">No document uploaded</p>;
        }
        return (
            <AntUpload
                accept=".doc,.docx,.pdf,image/*"
                maxCount={1}
                beforeUpload={() => false}
                onChange={({ fileList }) => {
                    if (fileList.length > 0 && fileList[0].originFileObj) {
                        onFileChange(fileList[0].originFileObj);
                    }
                }}
                showUploadList={false}
                className={cn("block", compact ? "w-auto" : "w-full [&_.ant-upload]:w-full")}
            >
                <button
                    type="button"
                    className={cn(
                        "flex items-center justify-center gap-2 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-600 dark:text-gray-300 hover:border-sidebar-primary hover:bg-sidebar/5 transition-colors",
                        compact ? "h-9 px-3" : "w-full h-[72px] px-4"
                    )}
                >
                    <UploadCloudIcon className="w-4 h-4" />
                    {compact ? "Upload Document" : "Upload PDF, Word, or image"}
                </button>
            </AntUpload>
        );
    }

    const fileUrl = isFileObject ? URL.createObjectURL(attachment[0]) : attachment[0];
    const isImage = isFileObject
        ? attachment[0].type?.startsWith("image/")
        : /\.(jpg|jpeg|png|gif|bmp|webp)(\?|#|$)/i.test(String(attachment[0]).split("?")[0]);
    const fileName = displayFileLabel(isFileObject ? attachment[0] : null, fileUrl, isImage, label);

    return (
        <div className={cn(
            "flex justify-between items-center gap-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-background min-w-0 overflow-hidden",
            compact ? "p-1.5" : "p-3 gap-3"
        )}>
            <div className="flex items-center gap-3 flex-1 min-w-0">
                {isImage ? (
                    <Image
                        src={fileUrl}
                        alt={fileName}
                        width={44}
                        height={44}
                        className="rounded-md object-cover"
                        preview={{
                            visible: previewOpen,
                            src: fileUrl,
                            onVisibleChange: setPreviewOpen,
                        }}
                    />
                ) : (
                    <FileText className="w-8 h-8 text-blue-500 flex-shrink-0" />
                )}
                <span className="text-sm text-gray-700 dark:text-gray-300 truncate min-w-0" title={fileName}>
                    {fileName}
                </span>
            </div>
            <div className="flex shrink-0 gap-1">
                {isImage ? (
                    <button
                        type="button"
                        onClick={() => setPreviewOpen(true)}
                        className="p-2 text-blue-600 border border-blue-200 rounded-md hover:bg-blue-50 dark:border-blue-800 dark:hover:bg-blue-900/20"
                        aria-label="View image"
                    >
                        <Eye className="w-4 h-4" />
                    </button>
                ) : (
                    <a
                        href={fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-blue-600 border border-blue-200 rounded-md hover:bg-blue-50 dark:border-blue-800 dark:hover:bg-blue-900/20"
                        aria-label="View document"
                    >
                        <Eye className="w-4 h-4" />
                    </a>
                )}
                {!readOnly && (
                <Button
                    size="sm"
                    variant="outline"
                    onClick={onRemoveFile}
                    className="p-2 text-red-600 border-red-200 hover:bg-red-50 dark:border-red-800 dark:hover:bg-red-900/20"
                >
                    <Trash className="w-4 h-4" />
                </Button>
                )}
            </div>
        </div>
    );
};

QualificationDocumentContent.displayName = "QualificationDocumentContent";

export default QualificationDocumentContent;
